'use client'

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import {
  useInfiniteGalleryPhotosQuery,
  useGalleryByUrlStubQuery,
} from '@/generated/graphql'
import { LoadingSpinner } from '@/General/LoadingSpinner'
import { PhotoModal } from './PhotoModal'
import { Button } from '@/General/Button'
import { GalleryPhotoGrid } from './GalleryPhotoGrid'
import { PHOTO_HASH_PREFIX, PHOTOS_PER_PAGE } from './constants'

const GALLERY_PHOTO_HASH_CHANGE_EVENT = 'gallery-photo-hash-change'

const getPhotoIdFromHash = (hash: string) => {
  if (!hash.startsWith(`#${PHOTO_HASH_PREFIX}`)) {
    return null
  }

  const photoIdText = hash.replace(`#${PHOTO_HASH_PREFIX}`, '')

  if (!/^\d+$/.test(photoIdText)) {
    return null
  }

  const photoId = Number(photoIdText)

  return Number.isInteger(photoId) && photoId > 0 ? photoId : null
}

const getPhotoHashSnapshot = () => {
  if (typeof window === 'undefined') {
    return null
  }

  return getPhotoIdFromHash(window.location.hash)
}

const subscribeToPhotoHash = (onStoreChange: () => void) => {
  if (typeof window === 'undefined') {
    return () => {}
  }

  window.addEventListener('hashchange', onStoreChange)
  window.addEventListener(GALLERY_PHOTO_HASH_CHANGE_EVENT, onStoreChange)

  return () => {
    window.removeEventListener('hashchange', onStoreChange)
    window.removeEventListener(GALLERY_PHOTO_HASH_CHANGE_EVENT, onStoreChange)
  }
}

const notifyPhotoHashChange = () => {
  if (typeof window === 'undefined') {
    return
  }

  window.dispatchEvent(new Event(GALLERY_PHOTO_HASH_CHANGE_EVENT))
}

const replaceBrowserUrl = (path: string) => {
  if (typeof window === 'undefined') {
    return
  }

  const nextUrl = new URL(path, window.location.origin)

  if (
    nextUrl.pathname === window.location.pathname &&
    nextUrl.search === window.location.search &&
    nextUrl.hash === window.location.hash
  ) {
    return
  }

  window.history.replaceState(null, '', nextUrl)
  notifyPhotoHashChange()
}

const clearPhotoHash = () => {
  if (typeof window === 'undefined') {
    return
  }

  const nextPath = `${window.location.pathname}${window.location.search}`

  window.history.replaceState(null, '', nextPath)
  notifyPhotoHashChange()
}

interface GalleryViewerProps {
  gallerySlug: string
  page: number
}

export const GalleryViewer = ({ gallerySlug, page }: GalleryViewerProps) => {
  const initialPage = Math.max(page, 1)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const hashPhotoId = useSyncExternalStore(
    subscribeToPhotoHash,
    getPhotoHashSnapshot,
    () => null
  )
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(
    null
  )
  const [pendingModalIndex, setPendingModalIndex] = useState<number | null>(
    null
  )
  const [isModalOpen, setIsModalOpen] = useState(false)

  const {
    data: galleryData,
    isPending: isGalleryLoading,
    error: galleryError,
  } = useGalleryByUrlStubQuery({ urlStub: gallerySlug })

  const {
    data: photosData,
    fetchNextPage,
    hasNextPage,
    isPending: isPhotosLoading,
    isFetchingNextPage,
    error: photosError,
  } = useInfiniteGalleryPhotosQuery(
    {
      galleryId: galleryData?.galleryByUrlStub?.id || 0,
      page: initialPage,
    },
    {
      initialPageParam: { page: initialPage },
      enabled: !!galleryData?.galleryByUrlStub?.id,
      getNextPageParam: (lastPage, allPages) => {
        const totalLoaded = allPages.reduce(
          (total, loadedPage) => total + loadedPage.galleryPhotos.length,
          (initialPage - 1) * PHOTOS_PER_PAGE
        )

        if (totalLoaded >= lastPage.galleryPhotosCount) {
          return undefined
        }

        return { page: initialPage + allPages.length }
      },
      getPreviousPageParam: () => undefined,
    }
  )

  const photos = useMemo(
    () =>
      photosData?.pages.flatMap(loadedPage => loadedPage.galleryPhotos) || [],
    [photosData?.pages]
  )

  const totalPhotos = photosData?.pages[0]?.galleryPhotosCount || 0

  const hashModalIndex = useMemo(() => {
    if (hashPhotoId === null) {
      return null
    }

    const photoIndex = photos.findIndex(photo => photo.id === hashPhotoId)

    return photoIndex >= 0 ? photoIndex : null
  }, [hashPhotoId, photos])

  const loadNextPage = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return false
    }

    void fetchNextPage()
    return true
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const isModalLoadingMore =
    pendingModalIndex !== null &&
    photos[pendingModalIndex] == null &&
    isFetchingNextPage &&
    isModalOpen

  const currentModalIndex =
    selectedPhotoIndex === null
      ? hashModalIndex
      : pendingModalIndex !== null && photos[pendingModalIndex]
        ? pendingModalIndex
        : selectedPhotoIndex

  const isModalVisible = isModalOpen || hashModalIndex !== null

  const currentModalPhotoNumber =
    currentModalIndex === null
      ? null
      : (initialPage - 1) * PHOTOS_PER_PAGE + currentModalIndex + 1

  const getPhotoPath = useCallback(
    (index: number) => {
      const photo = photos[index]

      if (!photo) {
        return null
      }

      const photoNumber = (initialPage - 1) * PHOTOS_PER_PAGE + index + 1
      const photoPage = Math.floor((photoNumber - 1) / PHOTOS_PER_PAGE) + 1

      return `/magic-conventions/gallery/${gallerySlug}/?page=${photoPage}#${PHOTO_HASH_PREFIX}${photo.id}`
    },
    [gallerySlug, initialPage, photos]
  )

  const currentPhotoPath =
    currentModalIndex === null ? null : getPhotoPath(currentModalIndex)

  const currentPhotoUrl =
    currentPhotoPath && typeof window !== 'undefined'
      ? new URL(currentPhotoPath, window.location.origin).href
      : currentPhotoPath

  useEffect(() => {
    const node = loadMoreRef.current

    if (!node || !hasNextPage || typeof IntersectionObserver === 'undefined') {
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          loadNextPage()
        }
      },
      { rootMargin: '600px 0px' }
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [hasNextPage, loadNextPage])

  useEffect(() => {
    if (!currentPhotoPath || !isModalVisible) {
      return
    }

    replaceBrowserUrl(currentPhotoPath)
  }, [currentPhotoPath, isModalVisible])

  if (isGalleryLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    )
  }

  if (galleryError) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">
          Error loading gallery:{' '}
          {galleryError instanceof Error
            ? galleryError.message
            : 'Unknown error'}
        </p>
      </div>
    )
  }

  if (!galleryData?.galleryByUrlStub) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">
          Gallery not found. URL stub: {gallerySlug}
        </p>
      </div>
    )
  }

  const gallery = galleryData.galleryByUrlStub

  const openModal = (index: number) => {
    setSelectedPhotoIndex(index)
    setPendingModalIndex(null)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedPhotoIndex(null)
    setPendingModalIndex(null)
    clearPhotoHash()
  }

  const navigatePhoto = (direction: 'prev' | 'next') => {
    if (currentModalIndex === null || photos.length === 0) return

    if (direction === 'next') {
      const nextIndex = currentModalIndex + 1

      if (nextIndex < photos.length) {
        setPendingModalIndex(null)
        setSelectedPhotoIndex(nextIndex)
        return
      }

      if (hasNextPage) {
        setPendingModalIndex(nextIndex)
        loadNextPage()
        return
      }

      setPendingModalIndex(null)
      setSelectedPhotoIndex(0)
      return
    }

    const previousIndex =
      currentModalIndex === 0 ? photos.length - 1 : currentModalIndex - 1

    setPendingModalIndex(null)
    setSelectedPhotoIndex(previousIndex)
  }

  return (
    <>
      {isPhotosLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : photosError ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Unable to load photos.</p>
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">No photos available in this gallery.</p>
        </div>
      ) : (
        <>
          <GalleryPhotoGrid photos={photos} onPhotoOpen={openModal} />

          {(hasNextPage || isFetchingNextPage) && (
            <div ref={loadMoreRef} className="flex justify-center py-4">
              {isFetchingNextPage ? (
                <LoadingSpinner />
              ) : (
                <Button onClick={() => loadNextPage()} color="blue" outline>
                  Load more photos
                </Button>
              )}
            </div>
          )}

          {gallery.photographer && (
            <p className="border-t pt-4 mt-8 text-gray-600">
              Photographer: {gallery.photographer}
            </p>
          )}
        </>
      )}

      {isModalVisible &&
        currentModalIndex !== null &&
        currentModalPhotoNumber !== null &&
        photos.length > 0 && (
          <PhotoModal
            photos={photos}
            currentIndex={currentModalIndex}
            currentPhotoNumber={currentModalPhotoNumber}
            photoUrl={currentPhotoUrl}
            totalPhotos={totalPhotos}
            onClose={closeModal}
            onNavigate={navigatePhoto}
            isLoadingMore={isModalLoadingMore}
          />
        )}
    </>
  )
}
